import { type ComponentType, type CSSProperties } from 'react';

import {
  ClientsFragment,
  DayFragment,
  InvoicesFragment,
  OrbitFragment,
  PublicationsFragment,
} from './bento-fragments';
import { SectionLabel } from './section-label';

interface Feature {
  title: string;
  text: string;
  Fragment: ComponentType;
  className: string;
  /** Onde o recorte encosta: no canto (sangra pela borda) ou centralizado. */
  align: 'corner' | 'center';
}

const features: Feature[] = [
  {
    title: 'Central do dia',
    text: 'Os prazos que vencem, as publicações novas e as tarefas da equipe assim que você abre o sistema.',
    Fragment: DayFragment,
    className: 'lg:col-span-7',
    align: 'corner',
  },
  {
    title: 'Publicações monitoradas',
    text: 'O DJEN acompanhado pelas OABs do escritório e vinculado ao processo pelo número CNJ.',
    Fragment: PublicationsFragment,
    className: 'lg:col-span-5',
    align: 'center',
  },
  {
    title: 'Histórico de cada cliente',
    text: 'Contatos, processos, documentos e financeiro numa ficha só, com tudo o que aconteceu.',
    Fragment: ClientsFragment,
    className: 'lg:col-span-4',
    align: 'corner',
  },
  {
    title: 'Honorários sem planilha',
    text: 'Timesheet, faturas numeradas com PDF, parcelamento e recebimentos no mesmo lugar.',
    Fragment: InvoicesFragment,
    className: 'lg:col-span-4',
    align: 'center',
  },
  {
    title: 'Integrações',
    text: 'DJEN, DataJud, anúncios do Meta e do Google, WhatsApp e assinatura eletrônica conectados.',
    Fragment: OrbitFragment,
    className: 'lg:col-span-4',
    align: 'center',
  },
];

export function FeaturesBento() {
  return (
    <section
      id="recursos"
      aria-labelledby="lp-features-title"
      className="scroll-mt-20 bg-white pb-24 sm:pb-32"
    >
      <div className="mx-auto max-w-[1180px] px-5 sm:px-6">
        <div className="mx-auto max-w-[760px] text-center" data-reveal>
          <SectionLabel>Recursos</SectionLabel>
          <h2
            id="lp-features-title"
            className="text-(--lp-ink) mt-5 text-balance text-[clamp(32px,4.6vw,52px)] font-medium leading-[1.08] tracking-[-0.04em]"
          >
            Tudo o que o escritório precisa, <span className="lp-gradient-text">num só lugar</span>
          </h2>
          <p className="text-(--lp-body) mx-auto mt-5 max-w-[560px] text-[17px] leading-relaxed">
            Cliente, processo, prazo, documento e honorário conversam entre si. Você para de
            procurar informação e volta a advogar.
          </p>
        </div>

        <div className="mt-14 grid gap-4 lg:grid-cols-12">
          {features.map(({ title, text, Fragment, className, align }, index) => (
            <article
              key={title}
              data-reveal
              style={{ '--delay': index % 3 } as CSSProperties}
              className={`bg-(--lp-soft) flex min-h-[400px] flex-col overflow-hidden rounded-[28px] ${className}`}
            >
              <div className="p-7 pb-6">
                <h3 className="text-(--lp-ink) text-[21px] font-medium tracking-[-0.02em]">
                  {title}
                </h3>
                <p className="text-(--lp-body) mt-2 max-w-[440px] text-[15px] leading-relaxed">
                  {text}
                </p>
              </div>
              <div
                aria-hidden="true"
                className={`mt-auto flex ${align === 'corner' ? 'justify-end' : 'justify-center pb-7'}`}
              >
                <Fragment />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
